import { Section } from '../../../components/Section/Section';
import {
  Avatar,
  Box,
  Button,
  ButtonProps,
  Icon,
  Stack,
  Typography,
} from '@mui/material';
import Image from 'next/image';
import React, { useState } from 'react';
import { WordIntervalTypography } from '../../../components/Content/WordInterval';
import AppLink from '../../../components/Link/AppLink';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { HighLightedText } from '../../../components/HighLightedText/HighLightedText';
import { DemoCTA } from '../../../components/Buttons/DemoCTA';
import { MarqtingPage } from '../../../components/Page/MarqtingPage';
import { useWindowDimensions } from '../../../hooks';
import InsightsIcon from '@mui/icons-material/Insights';
import EuroIcon from '@mui/icons-material/Euro';
import { MARQTING_FUNCTIONALITIES_ROUTE } from '../../routes';

export default function IndexPage() {
  return (
    <MarqtingPage
      selectedTab={0}
      title={
        'Maakt de offline wereld meetbaar. Dat zorgt voor betere beslissingen en meer rendement. | MarQting'
      }
      seo={{
        description:
          'Met passie en enthousiasme helpen wij organisaties met het invullen van ontbrekende puzzelstukken. We realiseren maximaal rendement door de brug te slaan tussen online en offline. Hier is MarQting het voorbeeld van. Fysieke producten of middelen maken wij meetbaar. Met slimme labels die altijd aanpasbaar zijn en meetbaar blijven.',
        openGraph: {
          type: 'website',
          locale: 'nl_NL',
          title:
            'Maakt de offline wereld meetbaar. Dat zorgt voor betere beslissingen en meer rendement. | MarQting | AppCenter Nederland',
          description:
            'Met passie en enthousiasme helpen wij organisaties met het invullen van ontbrekende puzzelstukken. We realiseren maximaal rendement door de brug te slaan tussen online en offline. Hier is MarQting het voorbeeld van. Fysieke producten of middelen maken wij meetbaar. Met slimme labels die altijd aanpasbaar zijn en meetbaar blijven.',
          images: [
            {
              url: 'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/featured_img_marqting.jpg',
              secureUrl:
                'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/featured_img_marqting.jpg',
              width: 1920,
              height: 1080,
              alt: 'Met passie en enthousiasme helpen wij organisaties met het invullen van ontbrekende puzzelstukken. We realiseren maximaal rendement door de brug te slaan tussen online en offline. Hier is MarQting het voorbeeld van. Fysieke producten of middelen maken wij meetbaar. Met slimme labels die altijd aanpasbaar zijn en meetbaar blijven.',
              type: 'image/jpeg',
            },
          ],
          site_name: 'AppCenter Nederland',
        },
      }}
    >
      <Section1 />
      <Section2 />
      <Section3 />
    </MarqtingPage>
  );
}

export function Section1() {
  const [visible, setVisible] = useState<boolean>(false);
  const { isMobile } = useWindowDimensions();
  const Usp = ({ icon, label }) => (
    <Stack component="span" direction={'row'} spacing={2}>
      <Avatar
        component="span"
        variant={'rounded'}
        sx={{ bgcolor: 'white', width: 50, height: 50 }}
      >
        <Icon color={'primary'}>{icon}</Icon>
      </Avatar>
      <Typography
        sx={{ display: 'flex', alignItems: 'center' }}
        component="span"
        variant={'body1'}
      >
        {label}
      </Typography>
    </Stack>
  );

  return (
    <Section
      position="left"
      contentContainerProps={{ maxWidth: 'lg' }}
      onChangeInViewport={setVisible}
      SectionBoxProps={{
        pb: { xs: 35, lg: 0 },
        sx: {
          backgroundSize: {
            xs: '90%',
            sm: '60%',
            md: '45%',
            lg: '42%',
            xl: '42%',
          },
          backgroundPosition: { xs: 'center bottom 40px', lg: 'bottom right' },
        },
      }}
      backgroundColor={'primary.main'}
      backgroundImage="https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/marqting_image_first.svg"
      caption={
        <>
          <Box sx={{ pb: 2 }}>
            <Image
              alt="logo"
              src="https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/marqting_logo_white.svg"
              height="70"
              width="300"
            />
          </Box>
          Neem betere strategische beslissingen op basis van data.
        </>
      }
      titleTypographyProps={{ lineHeight: 1.5 }}
      title={
        <>
          Maakt de offline wereld{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="meetbaar."
          />{' '}
          Dat zorgt voor betere beslissingen en {!isMobile ? ' ' : <br />}
          <WordIntervalTypography
            disabled={!visible}
            TypographyProps={{ color: 'primary' }}
            words={['meer', 'efficiëntere', 'exponentiële']}
          />{' '}
          <WordIntervalTypography
            disabled={!visible}
            TypographyProps={{ color: 'primary' }}
            words={['rendement.', 'campagnes.', 'omzetgroei.']}
          />
        </>
      }
      subtitle={
        <span>
          Met <strong>passie en enthousiasme</strong> helpen wij organisaties
          met het invullen van <strong>ontbrekende puzzelstukken.</strong> We
          realiseren <strong>maximaal rendement</strong> door de brug te slaan
          tussen online en offline. Hier is <strong>MarQting</strong> het
          voorbeeld van. <strong>Fysieke producten</strong> of{' '}
          <strong>middelen</strong> maken wij <strong>meetbaar.</strong> Met{' '}
          <strong>slimme labels</strong> die altijd aanpasbaar zijn en{' '}
          <strong>meetbaar blijven.</strong>
          <Stack component="span" direction="column" spacing={1} sx={{ mt: 2 }}>
            <Typography variant={'body1'} fontWeight={600} sx={{ mb: 2 }}>
              Het gebruik van MarQting zorgt voor:
            </Typography>
            <Usp
              icon={<InsightsIcon />}
              label={
                <Typography component="span">
                  <strong>Betere strategische keuzes</strong> op basis van{' '}
                  <strong>automatisch verzamelde data.</strong>
                </Typography>
              }
            />
            <Usp
              icon={<EuroIcon />}
              label={
                <Typography component="span">
                  <strong>Inzicht</strong> in investering versus rendement van{' '}
                  <strong>offline promoties.</strong>
                </Typography>
              }
            />
          </Stack>
        </span>
      }
      actions={[
        <DiscoverCTA key={0} />,
        <DemoCTA app={'marqting'} key={1} variant="outlined" color="inherit" />,
      ]}
    />
  );
}

export function Section2() {
  const { isMobile } = useWindowDimensions();
  return (
    <Section
      position="center"
      backgroundImage={
        'https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/marqting_image_second.svg'
      }
      backgroundPosition={'bottom center'}
      SectionContentBoxProps={{ sx: { pb: 0, pt: 10 } }}
      SectionBoxProps={{
        sx: {
          pb: { xs: 50, sm: 50, md: 60, lg: 70, xl: 80 },
          mt: 2,
          backgroundSize: {
            xs: '90%',
            sm: '60%',
            md: '55%',
            lg: '50%',
            xl: '40%',
          },
        },
      }}
      caption="MarQting helpt jou om betere strategische marketingkeuzes te maken."
      titleTypographyProps={{ lineHeight: 1.5 }}
      title={
        <Typography
          variant={'h1'}
          fontSize={isMobile ? '2rem !important' : '3rem'}
          fontWeight={600}
          component={'span'}
        >
          Het{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="ontbrekende"
          />{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="puzzelstuk"
          />{' '}
          in offline marketing. Maak de fysieke wereld{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="meetbaar"
          />{' '}
          op een{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="unieke"
          />{' '}
          <HighLightedText
            TypographyProps={{ color: 'primary' }}
            text="manier."
          />
        </Typography>
      }
    />
  );
}

export function Section3() {
  const { isMobile } = useWindowDimensions();
  const [visible, setVisible] = useState<boolean>(false);
  return (
    <Section
      onChangeInViewport={setVisible}
      backgroundImage="https://appcenternederland-general.ams3.cdn.digitaloceanspaces.com/appcenternederland-website/marqting_image_third.svg"
      contentContainerProps={{ maxWidth: isMobile ? 'lg' : 'md' }}
      SectionBoxProps={{
        sx: {
          backgroundPosition: {
            xs: 'center bottom -45px',
            lg: 'right bottom -45px',
          },
          pb: { xs: 50, lg: 4 },
          pt: { xs: 4, lg: 4 },
          backgroundSize: { xs: '100%', sm: '70%', md: '50%', lg: '40%' },
        },
      }}
      position="left"
      title={
        <>
          MarQting maakt de fysieke wereld oneindig dynamisch met
          {isMobile ? ' ' : <br />}
          <WordIntervalTypography
            disabled={!visible}
            TypographyProps={{ color: 'primary' }}
            words={['slimme', 'meetbaar', 'actuele']}
          />{' '}
          <WordIntervalTypography
            disabled={!visible}
            TypographyProps={{ color: 'primary' }}
            words={['visitekaartjes.', 'drukwerk.', 'productinformatie.']}
          />
        </>
      }
      subtitle={
        'Door de inzet van de slimme labels van MarQting is informatie altijd achteraf aanpasbaar. Labels verlopen nooit en blijven altijd werken. Klanten krijgen gemakkelijk specifieke informatie direct op de eigen telefoon. Positioneer producten slim in showrooms en laat gedrukte advertenties leven.'
      }
      caption={'FYSIEK EN MEETBAAR MET DIRECT INZICHT IN RESULTAAT.'}
      actions={[
        <DiscoverCTA color={'primary'} key={0} />,
        <DemoCTA app={'marqting'} key={1} variant="outlined" color="primary" />,
      ]}
    />
  );
}

const DiscoverCTA = (props?: ButtonProps) => (
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-ignore
  <Button
    fullWidth
    variant="contained"
    size={'large'}
    href={MARQTING_FUNCTIONALITIES_ROUTE}
    component={AppLink}
    color="secondary"
    endIcon={<ArrowForwardIcon />}
    {...props}
  >
    Ontdek de Functionaliteiten
  </Button>
);
